import React, { useEffect, useState, useRef } from "react";
import { Table, message, Button, Input, Switch, Tag } from "antd";
import { API_BASE_URL } from "../api/api";
import update from "immutability-helper";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import * as XLSX from "xlsx";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExportOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const DRAGGABLE_COLUMN_TYPE = "DragableBodyColumn";

const DragableHeaderCell = ({
  index,
  moveColumn,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef();

  const [{ isOver }, drop] = useDrop({
    accept: DRAGGABLE_COLUMN_TYPE,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    hover(item) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: DRAGGABLE_COLUMN_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  const draggingStyle = isDragging
    ? {
        background: "#e6f7ff",
        border: "2px dashed #1890ff",
        opacity: 0.7,
      }
    : isOver
    ? { background: "#f0f5ff" }
    : {};

  return (
    <th
      ref={ref}
      className={className}
      style={{
        cursor: "move",
        userSelect: "none",
        ...style,
        ...draggingStyle,
      }}
      {...restProps}
    />
  );
};

const UserPage = () => {
  const [userList, setUserList] = useState([]);
  const [filteredUserList, setFilteredUserList] = useState([]);
  const [userTableColumns, setUserTableColumns] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchUserList = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}user`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const users = result.data || [];
      setUserList(users);
      setFilteredUserList(users);
    } catch (error) {
      console.error(error);
      message.error("Failed to load users");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserList();
    setUserTableColumns(initialUserColumns);
  }, []);

  useEffect(() => {
    const lower = searchKeyword.toLowerCase();
    const filtered = userList.filter((user) => {
      return (
        String(user.member_id).toLowerCase().includes(lower) ||
        (user.email || "").toLowerCase().includes(lower) ||
        (user.user_xing || "").toLowerCase().includes(lower) ||
        (user.user_ming || "").toLowerCase().includes(lower) ||
        (user.mobile || "").toLowerCase().includes(lower)
      );
    });
    setFilteredUserList(filtered);
  }, [searchKeyword, userList]);

  const initialUserColumns = [
    {
      title: "ID",
      dataIndex: "user_id",
      key: "user_id",
      sorter: (a, b) => (a.user_id || 0) - (b.user_id || 0),
    },
    {
      title: "Member ID",
      dataIndex: "member_id",
      key: "member_id",
      render: (id) => <Link to={`/user/${id}`}>{id}</Link>,
      sorter: (a, b) => (a.member_id || 0) - (b.member_id || 0),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "First Name",
      dataIndex: "user_xing",
      key: "user_first_name",
      sorter: (a, b) => (a.user_xing || "").localeCompare(b.user_xing || ""),
    },
    {
      title: "Last Name",
      dataIndex: "user_ming",
      key: "user_last_name",
      sorter: (a, b) => (a.user_ming || "").localeCompare(b.user_ming || ""),
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      sorter: (a, b) => (a.mobile || "").localeCompare(b.mobile || ""),
    },
    {
      title: "Date of Birth",
      dataIndex: "user_dob",
      key: "user_dob",
      sorter: (a, b) => (a.user_dob || "").localeCompare(b.user_dob || ""),
    },
    {
      title: "Account Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        if (record.member_is_del === 1) {
          return <Tag color="gray">Deleted</Tag>;
        }

        return (
          <Tag color={status === 1 ? "green" : "red"}>
            {status === 1 ? "Active" : "Inactive"}
          </Tag>
        );
      },
      renderExport: (status, record) => {
        if (record.member_is_del === 1) return "Deleted";
        return status === 1 ? "Active" : "Inactive";
      },
      sorter: (a, b) => {
        const aVal = a.member_is_del === 1 ? -1 : a.status;
        const bVal = b.member_is_del === 1 ? -1 : b.status;
        return aVal - bVal;
      },
    },
    {
      title: "Membership Plan",
      dataIndex: "membership_level_id",
      key: "membership_level_id",
      render: (level) => {
        if (level === 1) {
          return (
            <span
              style={{
                color: "black",
                backgroundColor: "#f0f0f0",
                padding: "4px 8px",
                borderRadius: "8px",
                fontWeight: 500,
                display: "inline-block",
              }}
            >
              Basic
            </span>
          );
        } else if (level === 2) {
          return (
            <span
              style={{
                color: "white",
                backgroundColor: "orange",
                padding: "4px 8px",
                borderRadius: "8px",
                fontWeight: 500,
                display: "inline-block",
              }}
            >
              Gold
            </span>
          );
        } else {
          return null;
        }
      },
      renderExport: (level) =>
        level === 1 ? "Basic" : level === 2 ? "Gold" : null,
      sorter: (a, b) =>
        (a.membership_level_id || 0) - (b.membership_level_id || 0),
    },
    {
      title: "Membership Start Time",
      dataIndex: "membership_start_date",
      key: "membership_start_date",
      sorter: (a, b) =>
        (a.membership_start_date || "").localeCompare(
          b.membership_start_date || ""
        ),
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Membership End Time",
      dataIndex: "membership_end_date",
      key: "membership_end_date",
      sorter: (a, b) =>
        (a.membership_end_date || "").localeCompare(
          b.membership_end_date || ""
        ),
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Membership Auto Renew",
      dataIndex: "auto_renew",
      key: "auto_renew",
      render: (renew) =>
        renew === 1 ? (
          <CheckCircleTwoTone twoToneColor="#52c41a" />
        ) : renew === 0 ? (
          <CloseCircleTwoTone twoToneColor="#ff4d4f" />
        ) : null,
      renderExport: (renew) =>
        renew === 1 ? "Yes" : renew === 0 ? "No" : null,
      sorter: (a, b) => (a.auto_renew || 0) - (b.auto_renew || 0),
    },
    {
      title: "Member Points",
      dataIndex: "member_points",
      key: "member_points",
      sorter: (a, b) => (a.member_points || 0) - (b.member_points || 0),
    },
    {
      title: "Registered At",
      dataIndex: "add_time",
      key: "add_time",
      sorter: (a, b) => (a.add_time || "").localeCompare(b.add_time || ""),
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Last Login",
      dataIndex: "last_login_time",
      key: "last_login_time",
      sorter: (a, b) =>
        (a.last_login_time || "").localeCompare(b.last_login_time || ""),
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const moveColumn = (dragIndex, hoverIndex) => {
    const dragColumn = userTableColumns[dragIndex];
    setUserTableColumns(
      update(userTableColumns, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragColumn],
        ],
      })
    );
  };

  const tableComponents = {
    header: {
      cell: (props) => (
        <DragableHeaderCell {...props} moveColumn={moveColumn} />
      ),
    },
  };

  const finalUserColumns = userTableColumns.map((column, index) => ({
    ...column,
    onHeaderCell: () => ({ index, moveColumn }),
  }));

  const handleExportToExcel = () => {
    const exportData = filteredUserList.map((user) => {
      const rowData = {};
      userTableColumns.forEach((col) => {
        const value = user[col.dataIndex];
        rowData[col.title] = col.renderExport
          ? col.renderExport(value, user)
          : value;
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(workbook, "user_list.xlsx");
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <h2 style={{ marginBottom: 10 }}>User List</h2>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Input
            placeholder="Search by Member ID, Email, Name or Mobile"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            allowClear
            style={{ marginBottom: 16, width: 400 }}
          />
          <Button
            type="primary"
            onClick={handleExportToExcel}
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "8px",
              backgroundColor: "rgba(0, 128, 0, 0.1)", // Soft green background
              color: "#008000", // Strong green text color
              border: "1px solid #008000", // Green border
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              transition: "background-color 0.3s ease, transform 0.3s ease",
            }}
            icon={<ExportOutlined />}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.2)")
            } // Slightly darker green on hover
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.1)")
            } // Revert to soft green
          >
            Export to Excel
          </Button>
        </div>
        <div
          style={{
            margin: "16px 0",
            padding: "8px 16px",
            backgroundColor: "#fafafa",
            border: "1px solid #d9d9d9",
            borderRadius: "8px",
            fontWeight: 500,
            fontSize: "14px",
            color: "#555",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          Showing{" "}
          <span style={{ color: "#1890ff", fontWeight: 600 }}>
            {filteredUserList.length}
          </span>{" "}
          result{filteredUserList.length !== 1 ? "s" : ""} found
        </div>
        <div className="compact-table-wrapper">
          <Table
            columns={finalUserColumns}
            components={tableComponents}
            dataSource={filteredUserList}
            loading={isLoading}
            rowKey="user_id"
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
            }}
          />
        </div>
      </div>
    </DndProvider>
  );
};

export default UserPage;
